/**
 * Chart Capability Registry — single source of truth for every chart
 * type the Excel AI agent can produce.
 *
 * Why this exists
 * ---------------
 *
 * Before this module, chart capability lived in three disconnected
 * places:
 *
 * 1. 27 FactSet ActiveGraph `chartType` codes (e.g. `"P_PRICE"`,
 *    `"VAL_PE2"`) embedded as inline prose inside one chat-path
 *    system prompt. Reachable by the LLM only through that chat path.
 *
 * 2. 24 native Excel `xl*` chart types in
 *    `nativeChartOps.ts::SUPPORTED_XL_CHART_TYPES`. Reachable by the
 *    LLM only through the planner/executor `WorkbookModification.charts[]`
 *    surface (and only after the planner's 7-type simplified enum
 *    `bar|column|line|pie|area|scatter|doughnut` gets mapped to the
 *    `xl*` equivalent at apply time).
 *
 * 3. Nothing for ActiveGraphs in the edit path — the planner can only
 *    emit raw `<FDSChart …>…</FDSChart>` XML via
 *    `ActiveGraphModification.activeGraphContent`. The 27 FactSet
 *    codes are unreachable from the edit path.
 *
 * The asymmetry above is the root cause of the user-facing failure
 * mode tracked in `excel_ai_issues_and_fixes.md` as issue #2: the
 * model guesses chart codes from training data, the host rejects them
 * with `ChartTypeInvalid`, and the user sees a red placeholder bubble
 * with no recovery path. The model also overclaims capability because
 * it has no programmatic way to ground the answer to "what charts can
 * you make?".
 *
 * This registry is the foundation for fixing that. Everything else
 * (the prompt block, the validation gate at the host boundary, the
 * `list_chart_types` LLM tool, and eventually the unified
 * `ChartRequest` schema with a strict `json_schema` enum on
 * `chartTypeId`) derives from these typed entries.
 *
 * Keeping the registry in sync
 * ----------------------------
 *
 * The 27 FactSet codes are mirrored from the FactSet ChartGPT skill
 * (`f2_docs/charting/chart-creator-api-parameter-skill.md` §1) which
 * lives outside this repo. When FactSet ships a new chart type, add
 * an entry here. When FactSet renames or removes one, edit/remove the
 * entry here. The previous behaviour (manually editing prose in the
 * chat prompt and hoping all readers stay in sync) is no longer the
 * source of truth.
 *
 * The native list reuses `SUPPORTED_XL_CHART_TYPES` from
 * `nativeChartOps.ts`. Excel COM exposes ~70 `xl*` enum values; we
 * deliberately surface only the 24 we've verified through the
 * `nativeChartOps` normalizer's path. Expanding the native surface is
 * a deliberate decision (see follow-up work in
 * `excel_ai_issues_and_fixes.md`).
 */

import { SUPPORTED_XL_CHART_TYPES } from "../shared/charts/nativeChartTypes";

/**
 * Legacy host-family classification retained for existing validation callers.
 *
 * Model-facing routing must use `ChartOutputMode`: a FactSet chart type can
 * land as either an ActiveGraph or a native Chart Creator workbook, so this
 * two-value field is not sufficient to choose an output.
 *
 * These are different on-sheet objects with different editors and
 * different refresh behaviour — see the discussion in
 * `excel_ai_issues_and_fixes.md` for the user-visible distinction.
 */
export type ChartSystem = "factset_activegraph" | "native_excel";

/**
 * Where a capability sources its data.
 *
 *  - `factset_api` — pulls live data from FactSet (price history,
 *    valuation multiples, estimates, financials).
 *  - `workbook_range` — plots numbers/labels the user already has in
 *    cells.
 *  - `raw_xml` — caller supplies pre-rendered `<FDSChart …>` XML
 *    (e.g. the user pasted markup into chat). No parameter-driven
 *    build; the dispatcher just embeds the XML.
 */
export type ChartDataSource = "factset_api" | "workbook_range" | "raw_xml";

/** Model-facing chart output modes. Chart types may support more than one mode. */
export type ChartOutputMode = "range_chart" | "active_graph" | "native_chart";

/** Where the finished chart lands. */
export type ChartLandingLocation = "current_workbook" | "new_workbook";

/** Data contract used to build a chart in a particular output mode. */
export type ChartOutputDataSource = "workbook_range" | "factset_parameters";

export type SummaryTableKind = "performance" | "multiples";

export interface ChartSummaryTableMetadata {
  supportedIn: readonly ["native_chart"];
  kind: SummaryTableKind;
  defaultDisplay: "Horizontal";
  layouts: readonly ["Horizontal", "Vertical", "No"];
  maxPeriods: 5;
  editSemantics: string;
}

export interface ChartOutputModeCapability {
  id: ChartOutputMode;
  displayName: string;
  description: string;
  dataSource: ChartOutputDataSource;
  landingLocation: ChartLandingLocation;
  chartObject: "native_excel_chart" | "factset_activegraph_ole";
  supportedOperations: readonly string[];
  editSemantics: string;
  summaryTable: {
    supported: boolean;
    applicableChartTypeIds: readonly string[];
    defaultDisplay?: "Horizontal";
    layouts?: readonly ["Horizontal", "Vertical", "No"];
    maxPeriods?: 5;
    editSemantics: string;
  };
}

export const SUMMARY_TABLE_CHART_TYPES = {
  performance: [
    "P_FX_RATE",
    "P_EX_RATIO",
    "P_INDEX",
    "P_RELATIVE",
    "EST_TGT_RTNG",
  ],
  multiples: [
    "VAL_EV_SALES2",
    "VAL_EV_EBITD2",
    "VAL_EVAL_EBIT",
    "VAL_PSALES2",
    "VAL_PE2",
    "VAL_PFCFE",
    "VAL_PBK2",
    "VAL_PBK_TANG",
    "VAL_DIV_YLD2",
  ],
} as const satisfies Record<SummaryTableKind, readonly string[]>;

export const SUMMARY_TABLE_CHART_TYPE_IDS: readonly string[] = [
  ...SUMMARY_TABLE_CHART_TYPES.performance,
  ...SUMMARY_TABLE_CHART_TYPES.multiples,
];

const SUMMARY_TABLE_KIND_BY_CHART_TYPE = new Map<string, SummaryTableKind>([
  ...SUMMARY_TABLE_CHART_TYPES.performance.map(
    (id) => [id, "performance"] as const,
  ),
  ...SUMMARY_TABLE_CHART_TYPES.multiples.map(
    (id) => [id, "multiples"] as const,
  ),
]);

export const CHART_OUTPUT_MODES: readonly ChartOutputModeCapability[] = [
  {
    id: "range_chart",
    displayName: "Range chart",
    description:
      "Native Excel chart sourced from an observed workbook range and kept in the current workbook.",
    dataSource: "workbook_range",
    landingLocation: "current_workbook",
    chartObject: "native_excel_chart",
    supportedOperations: [
      "read_range_chart",
      "create_range_chart",
      "update_range_chart",
      "add_range_chart_series",
      "update_range_chart_series",
      "remove_range_chart_series",
      "get_range_chart_image",
    ],
    editSemantics:
      "Edit the native chart object directly; series changes use the dedicated series operations.",
    summaryTable: {
      supported: false,
      applicableChartTypeIds: [],
      editSemantics: "Chart Creator summary tables do not apply to range charts.",
    },
  },
  {
    id: "active_graph",
    displayName: "FactSet ActiveGraph",
    description:
      "FactSet parameter-driven ActiveGraph OLE object inserted into the current workbook. Choose this over native_chart only when the user wants the chart to stay on the sheet they are working in, or explicitly asks for an ActiveGraph.",
    dataSource: "factset_parameters",
    landingLocation: "current_workbook",
    chartObject: "factset_activegraph_ole",
    supportedOperations: [
      "read_active_graph",
      "create_active_graph",
      "edit_active_graph",
      "list_active_graph_stylesets",
    ],
    editSemantics:
      "Regenerate Chart Creator XML from explicit FactSet parameters and update the selected ActiveGraph.",
    summaryTable: {
      supported: false,
      applicableChartTypeIds: [],
      editSemantics:
        "Native-workbook summary-table inputs are not exposed through the ActiveGraph tool.",
    },
  },
  {
    id: "native_chart",
    displayName: "Chart Creator native workbook",
    description:
      "FactSet parameter-driven native Excel chart delivered in a complete new workbook with its underlying data. Default for parameter-driven chart requests.",
    dataSource: "factset_parameters",
    landingLocation: "new_workbook",
    chartObject: "native_excel_chart",
    supportedOperations: [
      "create_native_chart",
      "read_native_chart",
      "update_native_chart",
    ],
    editSemantics:
      "Update re-requests the chart from FactSet with the change merged into the parameters it was created with and delivers it in another new workbook; the previous workbook keeps its old chart and manual tweaks made there are not carried over.",
    summaryTable: {
      supported: true,
      applicableChartTypeIds: SUMMARY_TABLE_CHART_TYPE_IDS,
      defaultDisplay: "Horizontal",
      layouts: ["Horizontal", "Vertical", "No"],
      maxPeriods: 5,
      editSemantics:
        "The summary table is part of the chart request, so Chart Creator builds it; periods can be set, swapped, or removed on a create or on a later update. FactSet is still rolling out support for the request field, so a table is reported as requested rather than as confirmed.",
    },
  },
];

export function getChartOutputModeCapability(
  mode: ChartOutputMode,
): ChartOutputModeCapability {
  const capability = CHART_OUTPUT_MODES.find((entry) => entry.id === mode);
  if (!capability) {
    throw new Error(`Unknown chart output mode: ${mode}`);
  }
  return capability;
}

/** Shared model-facing description used by chart tool definitions. */
export function buildChartToolDescription(mode: ChartOutputMode): string {
  const capability = getChartOutputModeCapability(mode);
  return (
    `${capability.description} Data source: ${capability.dataSource}. ` +
    `Landing location: ${capability.landingLocation}. ` +
    `Edit semantics: ${capability.editSemantics}`
  );
}

/**
 * Earnings basis represented by a chart capability when that distinction is
 * semantically material. Omitted for capabilities where an earnings basis
 * does not apply.
 */
export type ChartMetricBasis = "trailing_ltm" | "forward_estimate";

/**
 * Conservative entity-count contract for FactSet ActiveGraphs.
 *
 * This is intentionally only about the number of ticker/entity inputs
 * the Chart Creator request should carry. It does not describe native
 * Excel series counts.
 */
export type ActiveGraphEntityCardinality = "single" | "pair" | "multi";

/**
 * One concrete chart type the agent can emit. Pure data — no
 * runtime behaviour lives on these entries.
 */
export interface ChartCapability {
  /** Wire-level identifier. FactSet code (`"P_PRICE"`) or COM enum (`"xlColumnClustered"`). Passed verbatim to the host. */
  id: string;
  /** Human-readable label for prompts, tool output, and UI surfacing. */
  displayName: string;
  /** Which on-sheet chart system this belongs to. */
  system: ChartSystem;
  /** Data sources this chart type can be built from. Most entries are single-source today. */
  dataSources: readonly ChartDataSource[];
  /** Model-facing output modes that accept this chart type. */
  outputModes: readonly ChartOutputMode[];
  /** Loose grouping for filtering and prompt-block rendering. */
  category: ChartCategory;
  /** One-line description suitable for prompts and tool responses. */
  description: string;
  /**
   * Exact earnings basis represented by this capability, when applicable.
   * This prevents a generic metric label from hiding a trailing/forward
   * distinction.
   */
  metricBasis?: ChartMetricBasis;
  /**
   * User phrases / synonyms that should map to this chart type. Used
   * by `suggestChartCapability` when the model picks an unknown code
   * — we map the bad guess back to a likely-intended entry by
   * keyword.
   */
  aliases: readonly string[];
  /** FactSet ActiveGraph-only ticker cardinality, used to avoid semantically invalid edits. */
  activeGraphEntityCardinality?: ActiveGraphEntityCardinality;
  /** Native Chart Creator workbook summary-table metadata, when applicable. */
  summaryTable?: ChartSummaryTableMetadata;
}

export type ChartCategory =
  | "price_volume_fx"
  | "valuation"
  | "estimates"
  | "financials"
  | "fixed_income"
  | "size"
  | "native_column"
  | "native_bar"
  | "native_line"
  | "native_pie"
  | "native_area"
  | "native_scatter"
  | "native_other";

/**
 * Patterns we know the host does NOT support. Used to detect when the
 * model is trying for something out of scope and provide a focused
 * "we can't do that" reply instead of a generic invalid-code error.
 *
 * Match contract: lowercased substring against either the LLM-emitted
 * code or a free-form user phrase (see {@link isKnownUnsupportedChartType}).
 *
 * **Authoring rules for this list:**
 *   - Do NOT add bare 3-character stems. They false-positive on
 *     unrelated codes (e.g. `"map"` would flag `"ROADMAP"`,
 *     `"BITMAP"`, `"SITEMAPFLOW"` because the matcher is plain
 *     substring, not word-boundary). Use the longer specific phrasing
 *     (`"map chart"`, `"world map"`) instead.
 *   - Prefer full chart-family names (`"treemap"`, `"sankey"`) over
 *     fragments.
 */
export const UNSUPPORTED_CHART_PATTERNS = [
  "sankey",
  "treemap",
  "chord",
  "waterfall",
  "funnel",
  "candlestick",
  "ohlc",
  "boxplot",
  "box plot",
  "violin",
  // Map family — keep specific to avoid roadmap/bitmap/sitemap false
  // positives (see authoring rules above; Bugbot PR-201 #3267589997).
  "map chart",
  "world map",
  "geographic map",
  "geo chart",
  "choropleth",
  "heatmap",
  "network",
  "graph diagram",
  "gantt",
  "sunburst",
  "histogram",
] as const;

// ---------------------------------------------------------------------------
// FactSet Chart Creator capabilities (27 entries, shared by ActiveGraph and
// native-workbook output modes; mirrored from the chat
// prompt at `excelAIService.ts` lines 2262-2297).
//
// Source of truth upstream: FactSet ChartGPT skill —
// `f2_docs/charting/chart-creator-api-parameter-skill.md` §1.
// ---------------------------------------------------------------------------

const FACTSET_CAPABILITY_BASE: readonly Omit<
  ChartCapability,
  "outputModes" | "summaryTable"
>[] = [
  // Price / volume / FX
  {
    id: "P_PRICE",
    displayName: "Closing Price",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "price_volume_fx",
    description: "Closing price line chart for one or more tickers.",
    aliases: ["price", "stock price", "closing price", "share price"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "P_PRICEDUAL",
    displayName: "Closing Price (dual-axis)",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "price_volume_fx",
    description: "Two companies side-by-side on independent y-axes.",
    aliases: ["dual axis price", "two axis price", "compare price"],
    activeGraphEntityCardinality: "pair",
  },
  {
    id: "P_INDEX",
    displayName: "Indexed Price",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "price_volume_fx",
    description: "Base-100 indexed price for relative performance comparison.",
    aliases: ["indexed", "price performance", "relative performance", "rebased"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "P_PVOL",
    displayName: "Price + Volume",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "price_volume_fx",
    description: "Price line with daily volume bars below.",
    aliases: ["price and volume", "volume", "trading volume"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "P_RELATIVE",
    displayName: "Relative Strength",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "price_volume_fx",
    description: "Relative-strength comparison between tickers.",
    aliases: ["relative strength", "rs"],
    activeGraphEntityCardinality: "pair",
  },
  {
    id: "P_FX_RATE",
    displayName: "FX Rate",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "price_volume_fx",
    description: "Currency exchange rate over time.",
    aliases: ["fx", "currency", "exchange rate", "forex"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "P_EX_RATIO",
    displayName: "Price Exchange Ratio",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "price_volume_fx",
    description: "Price ratio between two tickers over time.",
    aliases: ["price ratio", "exchange ratio"],
    activeGraphEntityCardinality: "pair",
  },

  // Valuation multiples
  {
    id: "VAL_PE2",
    displayName: "Trailing P/E Ratio (LTM)",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description:
      "Trailing/LTM Price / Earnings ratio (NB: the FactSet code is `VAL_PE2`, not `P_PE`). " +
      "It does not represent forward/NTM P/E.",
    metricBasis: "trailing_ltm",
    aliases: [
      "pe",
      "p/e",
      "p e",
      "price to earnings",
      "pe ratio",
      "earnings ratio",
      "trailing pe",
      "trailing p/e",
      "ltm pe",
      "ltm p/e",
      "ttm pe",
      "ttm p/e",
    ],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_EV_EBITD2",
    displayName: "EV / EBITDA",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Enterprise Value / EBITDA multiple over time.",
    aliases: ["ev/ebitda", "ev ebitda", "enterprise value to ebitda"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_EV_SALES2",
    displayName: "EV / Sales",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Enterprise Value / Sales (or Revenue) multiple over time.",
    aliases: ["ev/sales", "ev/revenue", "ev sales", "ev revenue"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_EVAL_EBIT",
    displayName: "EV / EBIT",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Enterprise Value / EBIT multiple over time.",
    aliases: ["ev/ebit", "ev ebit"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_PBK2",
    displayName: "P/BV",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Price / Book Value ratio over time.",
    aliases: ["pb", "p/b", "p bv", "p/bv", "pb ratio", "price to book"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_PBK_TANG",
    displayName: "P/TBV",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Price / Tangible Book Value ratio over time.",
    aliases: ["p/tbv", "ptbv", "price to tangible book"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_PSALES2",
    displayName: "P/S",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Price / Sales ratio over time.",
    aliases: ["p/s", "ps", "price to sales"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_PFCFE",
    displayName: "P/FCF",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Price / Free Cash Flow (to equity) ratio over time.",
    aliases: ["p/fcf", "pfcf", "price to free cash flow", "fcf yield"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "VAL_DIV_YLD2",
    displayName: "Dividend Yield",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "valuation",
    description: "Dividend yield percentage over time.",
    aliases: ["dividend yield", "div yield", "yield"],
    activeGraphEntityCardinality: "multi",
  },
  {
    id: "SIZE_MKT_VAL",
    displayName: "Market Cap",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "size",
    description: "Market value / market capitalisation over time.",
    aliases: ["market cap", "mkt cap", "market capitalization", "market value", "market capitalisation"],
    activeGraphEntityCardinality: "multi",
  },

  // Estimates / earnings
  {
    id: "EST_EPS",
    displayName: "EPS Estimates & History",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "estimates",
    description: "Earnings-per-share history with analyst estimates overlay (NB: the FactSet code is `EST_EPS`, not `P_EPS`).",
    aliases: ["eps", "earnings per share", "eps estimates"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "EST_SURPRISE_HISTORY",
    displayName: "EPS Surprise History",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "estimates",
    description: "Historical EPS surprises (reported vs. estimate).",
    aliases: ["earnings surprise", "eps surprise", "beat miss"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "EST_EBITDA",
    displayName: "EBITDA Estimates",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "estimates",
    description: "Annual mean EBITDA estimates from analysts.",
    aliases: ["ebitda estimate", "ebitda forecast", "ebitda consensus"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "EST_NET_INC",
    displayName: "Net Income Estimates",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "estimates",
    description: "Annual mean Net Income estimates from analysts.",
    aliases: ["net income estimate", "net income forecast", "earnings estimate"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "EST_SALES",
    displayName: "Sales Estimates",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "estimates",
    description: "Annual mean Sales / Revenue estimates from analysts.",
    aliases: ["sales estimate", "revenue estimate", "sales forecast", "revenue forecast"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "EST_TGT_RTNG",
    displayName: "Analyst Targets & Ratings",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "estimates",
    description: "Analyst target prices and ratings over time.",
    aliases: ["analyst rating", "price target", "target price", "buy sell hold"],
    activeGraphEntityCardinality: "single",
  },

  // Financials / segments
  {
    id: "FIN_SALES",
    displayName: "Revenue History",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "financials",
    description: "Historical revenue / sales as a column chart.",
    aliases: ["revenue", "sales", "revenue history", "sales history", "top line"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "FIN_REV_BUS_SEG",
    displayName: "Revenue by Business Segment",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "financials",
    description: "Revenue breakdown by business segment (pie chart).",
    aliases: ["revenue by segment", "business segment", "segment breakdown", "revenue split"],
    activeGraphEntityCardinality: "single",
  },
  {
    id: "FIN_REV_GEO_SEG",
    displayName: "Revenue by Geography",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "financials",
    description: "Revenue breakdown by geographic segment (pie chart).",
    aliases: ["revenue by geography", "geographic segment", "geo breakdown", "revenue by region"],
    activeGraphEntityCardinality: "single",
  },

  // Fixed income
  {
    id: "FIX_YIELD_CURVE",
    displayName: "US Treasury Yield Curve",
    system: "factset_activegraph",
    dataSources: ["factset_api"],
    category: "fixed_income",
    description: "US Treasury yield curve (yield by maturity).",
    aliases: ["yield curve", "treasury curve", "us treasury yields", "rates curve"],
    activeGraphEntityCardinality: "single",
  },
];

const FACTSET_CAPABILITIES: readonly ChartCapability[] =
  FACTSET_CAPABILITY_BASE.map((capability) => {
    const summaryTableKind = SUMMARY_TABLE_KIND_BY_CHART_TYPE.get(capability.id);
    return {
      ...capability,
      outputModes: ["active_graph", "native_chart"] as const,
      ...(summaryTableKind
        ? {
            summaryTable: {
              supportedIn: ["native_chart"] as const,
              kind: summaryTableKind,
              defaultDisplay: "Horizontal" as const,
              layouts: ["Horizontal", "Vertical", "No"] as const,
              maxPeriods: 5 as const,
              editSemantics:
                "Layout and periods are set on the Chart Creator request, on a create or on a later update.",
            },
          }
        : {}),
    };
  });

// ---------------------------------------------------------------------------
// Range-chart Excel capabilities — derived from the verified
// `SUPPORTED_XL_CHART_TYPES` list in `nativeChartOps.ts`. Excel COM
// supports ~70 `xl*` enum values; we surface only the 24 the
// `nativeChartOps` normalizer has been validated against, to keep the
// agent's emit surface aligned with what the apply path can actually
// land. Expanding this is a deliberate follow-up (see
// `excel_ai_issues_and_fixes.md`).
// ---------------------------------------------------------------------------

interface NativeMetadata {
  displayName: string;
  category: ChartCategory;
  description: string;
  aliases: readonly string[];
}

const NATIVE_METADATA: Readonly<Record<string, NativeMetadata>> = {
  xlColumnClustered: {
    displayName: "Clustered Column",
    category: "native_column",
    description: "Vertical bars grouped per category.",
    aliases: ["column", "vertical bar", "column chart", "clustered column"],
  },
  xlColumnStacked: {
    displayName: "Stacked Column",
    category: "native_column",
    description: "Vertical bars stacked per category to show totals.",
    aliases: ["stacked column", "stacked vertical bar"],
  },
  xlColumnStacked100: {
    displayName: "100% Stacked Column",
    category: "native_column",
    description: "Stacked column showing percentage composition (always 100%).",
    aliases: ["100% column", "percentage column", "100 stacked column"],
  },
  xlBarClustered: {
    displayName: "Clustered Bar",
    category: "native_bar",
    description: "Horizontal bars grouped per category.",
    aliases: ["bar", "horizontal bar", "clustered bar"],
  },
  xlBarStacked: {
    displayName: "Stacked Bar",
    category: "native_bar",
    description: "Horizontal bars stacked per category to show totals.",
    aliases: ["stacked bar", "stacked horizontal bar"],
  },
  xlBarStacked100: {
    displayName: "100% Stacked Bar",
    category: "native_bar",
    description: "Stacked horizontal bar showing percentage composition (always 100%).",
    aliases: ["100% bar", "percentage bar", "100 stacked bar"],
  },
  xlLine: {
    displayName: "Line",
    category: "native_line",
    description: "Line chart connecting category data points.",
    aliases: ["line", "line chart", "trend"],
  },
  xlLineMarkers: {
    displayName: "Line with Markers",
    category: "native_line",
    description: "Line chart with visible markers at each data point.",
    aliases: ["line with markers", "marker line"],
  },
  xlLineStacked: {
    displayName: "Stacked Line",
    category: "native_line",
    description: "Stacked line chart showing cumulative values.",
    aliases: ["stacked line"],
  },
  xlLineStackedMarkers: {
    displayName: "Stacked Line with Markers",
    category: "native_line",
    description: "Stacked line chart with markers at each data point.",
    aliases: ["stacked line with markers"],
  },
  xlPie: {
    displayName: "Pie",
    category: "native_pie",
    description: "Pie chart showing parts of a whole.",
    aliases: ["pie", "pie chart"],
  },
  xlPieExploded: {
    displayName: "Exploded Pie",
    category: "native_pie",
    description: "Pie chart with slices pulled apart for emphasis.",
    aliases: ["exploded pie"],
  },
  xlDoughnut: {
    displayName: "Doughnut",
    category: "native_pie",
    description: "Doughnut chart — pie chart with a hollow centre.",
    aliases: ["doughnut", "donut", "donut chart", "doughnut chart"],
  },
  xlDoughnutExploded: {
    displayName: "Exploded Doughnut",
    category: "native_pie",
    description: "Doughnut chart with segments pulled apart for emphasis.",
    aliases: ["exploded doughnut", "exploded donut"],
  },
  xlArea: {
    displayName: "Area",
    category: "native_area",
    description: "Area chart — line chart with filled area beneath.",
    aliases: ["area", "area chart"],
  },
  xlAreaStacked: {
    displayName: "Stacked Area",
    category: "native_area",
    description: "Stacked area chart showing cumulative composition over time.",
    aliases: ["stacked area"],
  },
  xlAreaStacked100: {
    displayName: "100% Stacked Area",
    category: "native_area",
    description: "Stacked area showing percentage composition (always 100%).",
    aliases: ["100% area", "percentage area", "100 stacked area"],
  },
  xlXYScatter: {
    displayName: "Scatter",
    category: "native_scatter",
    description: "Scatter plot of x/y point pairs.",
    aliases: ["scatter", "scatter plot", "xy scatter", "xy"],
  },
  xlXYScatterLines: {
    displayName: "Scatter with Lines",
    category: "native_scatter",
    description: "Scatter plot with connecting lines between points.",
    aliases: ["scatter with lines"],
  },
  xlXYScatterLinesNoMarkers: {
    displayName: "Scatter with Smooth Lines",
    category: "native_scatter",
    description: "Scatter with connecting lines and no point markers.",
    aliases: ["scatter smooth lines", "scatter no markers"],
  },
  xlBubble: {
    displayName: "Bubble",
    category: "native_other",
    description: "Bubble chart — x/y points with bubble size as a third dimension.",
    aliases: ["bubble", "bubble chart"],
  },
  xlBubble3DEffect: {
    displayName: "Bubble (3D Effect)",
    category: "native_other",
    description: "Bubble chart rendered with a 3D effect.",
    aliases: ["3d bubble", "bubble 3d"],
  },
  xlRadar: {
    displayName: "Radar",
    category: "native_other",
    description: "Radar / spider chart for multi-variable comparison.",
    aliases: ["radar", "spider", "spider chart", "radar chart"],
  },
  xlRadarFilled: {
    displayName: "Filled Radar",
    category: "native_other",
    description: "Radar chart with the enclosed area filled in.",
    aliases: ["filled radar", "filled spider"],
  },
};

const NATIVE_CAPABILITIES: readonly ChartCapability[] = SUPPORTED_XL_CHART_TYPES.map((id) => {
  const meta = NATIVE_METADATA[id];
  if (!meta) {
    // Defensive: if `SUPPORTED_XL_CHART_TYPES` grows without a metadata
    // entry here, fail loudly at import time rather than ship a
    // capability the prompt/tool will not describe usefully.
    throw new Error(
      `chartCapabilityRegistry: missing NATIVE_METADATA entry for SUPPORTED_XL_CHART_TYPE "${id}". ` +
        `Add an entry to NATIVE_METADATA or remove "${id}" from SUPPORTED_XL_CHART_TYPES.`,
    );
  }
  return {
    id,
    displayName: meta.displayName,
    system: "native_excel" as const,
    dataSources: ["workbook_range"] as const,
    outputModes: ["range_chart"] as const,
    category: meta.category,
    description: meta.description,
    aliases: meta.aliases,
  };
});

/**
 * The unique chart-type catalog. 27 FactSet Chart Creator entries + 24 Excel
 * xl* entries = 51 types. Output-mode support is attached to each entry, so
 * the 27 FactSet types are not duplicated for ActiveGraph/native-workbook use.
 */
export const CHART_CAPABILITIES: readonly ChartCapability[] = [
  ...FACTSET_CAPABILITIES,
  ...NATIVE_CAPABILITIES,
];

// ---------------------------------------------------------------------------
// Lookup helpers — pure functions, all O(n) over CHART_CAPABILITIES.
// 51 entries makes O(n) trivially fine and avoids a parallel index
// that would need to stay in sync with the data.
// ---------------------------------------------------------------------------

/**
 * Resolve a wire-level `id` to a capability. Exact-match only — used
 * by the validation gate at the host boundary. Returns `null` when
 * the id is not in the catalog.
 */
export function findChartCapability(id: string): ChartCapability | null {
  if (typeof id !== "string" || id.length === 0) return null;
  return CHART_CAPABILITIES.find((c) => c.id === id) ?? null;
}

export interface ListChartCapabilitiesFilter {
  outputMode?: ChartOutputMode;
  system?: ChartSystem;
  category?: ChartCategory;
  dataSource?: ChartDataSource;
}

/**
 * Filter the catalog. Used by the `list_chart_types` LLM tool and
 * the prompt-block builder. All filters are AND-ed.
 */
export function listChartCapabilities(
  filter: ListChartCapabilitiesFilter = {},
): readonly ChartCapability[] {
  return CHART_CAPABILITIES.filter((c) => {
    if (filter.outputMode && !c.outputModes.includes(filter.outputMode)) return false;
    if (filter.system && c.system !== filter.system) return false;
    if (filter.category && c.category !== filter.category) return false;
    if (filter.dataSource && !c.dataSources.includes(filter.dataSource)) return false;
    return true;
  });
}

export interface ActiveGraphEntityConstraint {
  cardinality: ActiveGraphEntityCardinality;
  minTickers: number;
  maxTickers?: number;
  label: string;
}

export function getActiveGraphEntityConstraint(
  chartType: string,
): ActiveGraphEntityConstraint | null {
  const capability = findChartCapability(chartType);
  if (capability?.system !== "factset_activegraph") return null;

  switch (capability.activeGraphEntityCardinality) {
    case "single":
      return {
        cardinality: "single",
        minTickers: 1,
        maxTickers: 1,
        label: "exactly one ticker",
      };
    case "pair":
      return {
        cardinality: "pair",
        minTickers: 2,
        maxTickers: 2,
        label: "exactly two tickers",
      };
    case "multi":
      return {
        cardinality: "multi",
        minTickers: 1,
        label: "one or more tickers",
      };
    default:
      return null;
  }
}

function activeGraphEntityPromptLabel(
  cardinality: ActiveGraphEntityCardinality | undefined,
): string {
  switch (cardinality) {
    case "single":
      return "entities: exactly one";
    case "pair":
      return "entities: exactly two";
    case "multi":
      return "entities: one or more";
    default:
      return "entities: see chart docs";
  }
}

const FORWARD_PE_QUALIFIER_SOURCE =
  String.raw`(?:forward|fwd|ntm|next[\s-]+(?:twelve|12)[\s-]+months?|fy\s*[-+]?\s*1)`;
const PE_METRIC_SOURCE =
  String.raw`(?:p\s*(?:\/|-)?\s*e(?:\s+ratio)?|pe\s+ratio|price\s*(?:\/|[-–—]?\s*to\s*[-–—]?)\s*earnings(?:\s+ratio)?)`;
const FORWARD_PE_REQUEST_PATTERN = new RegExp(
  String.raw`(?:\b${FORWARD_PE_QUALIFIER_SOURCE}\b[^.!?\n]{0,48}\b${PE_METRIC_SOURCE}\b|\b${PE_METRIC_SOURCE}\b[^.!?\n]{0,48}\b${FORWARD_PE_QUALIFIER_SOURCE}\b)`,
  "i",
);
const FORWARD_PE_EXCLUSION_PATTERNS = [
  new RegExp(
    String.raw`\b(?:not|without|excluding?)\s+(?:(?:a|an|the)\s+)?${FORWARD_PE_QUALIFIER_SOURCE}\b[^.!?\n]{0,32}\b${PE_METRIC_SOURCE}\b`,
    "gi",
  ),
  new RegExp(
    String.raw`\b(?:do\s+not|don't|never)\s+(?:use|show|chart|plot|include|create)\s+(?:(?:a|an|the)\s+)?${FORWARD_PE_QUALIFIER_SOURCE}\b[^.!?\n]{0,32}\b${PE_METRIC_SOURCE}\b`,
    "gi",
  ),
  new RegExp(
    String.raw`\b${PE_METRIC_SOURCE}\b[^.!?\n]{0,32}\b(?:but\s+)?not\s+(?:(?:a|an|the)\s+)?${FORWARD_PE_QUALIFIER_SOURCE}\b`,
    "gi",
  ),
] as const;

/**
 * Detects an explicit forward-estimate P/E request while preserving direct
 * trailing-only instructions such as "LTM P/E, not forward P/E".
 */
export function isExplicitForwardPeRequest(userRequest: string | undefined): boolean {
  if (typeof userRequest !== "string" || userRequest.trim().length === 0) {
    return false;
  }
  const positiveIntent = FORWARD_PE_EXCLUSION_PATTERNS.reduce(
    (remaining, pattern) => remaining.replace(pattern, " "),
    userRequest,
  );
  return FORWARD_PE_REQUEST_PATTERN.test(positiveIntent);
}

const TRAILING_PE_OVERRIDE_PATTERN = new RegExp(
  String.raw`(?:\b(?:trailing|ltm|ttm)\b[^.!?\n]{0,32}\b${PE_METRIC_SOURCE}\b|\b${PE_METRIC_SOURCE}\b[^.!?\n]{0,32}\b(?:trailing|ltm|ttm)\b)`,
  "i",
);
const TRAILING_PE_EXCLUSION_PATTERN = new RegExp(
  String.raw`\b(?:do\s+not|don't|never|no|not|without|exclude|excluding|avoid(?:ing)?)\s+(?:(?:use|using|show|showing|chart|charting|plot|plotting|include|including|substitute|substituting)\s+)?(?:(?:a|an|the|any)\s+)?(?:trailing|ltm|ttm)\b(?:[^.!?\n]{0,24}\b${PE_METRIC_SOURCE}\b)?`,
  "gi",
);
const REFERENTIAL_TRAILING_OVERRIDE_PATTERN = new RegExp(
  String.raw`\b(?:(?:use|prefer)\s+(?:(?:a|the)\s+)?(?:trailing|ltm|ttm)\b|make\s+(?:(?:it|that|this|the\s+(?:chart|series|metric|basis))\s+)(?:as\s+)?(?:trailing|ltm|ttm)\b|(?:switch|change)\s+(?:(?:it|that|this|the\s+(?:chart|series|metric|basis))\s+)?(?:to|into)\s+(?:trailing|ltm|ttm)\b|(?:show|chart|plot)\s+(?:(?:it|that|this)\s+)?(?:as\s+)?(?:trailing|ltm|ttm)\s+(?:instead|only)\b)`,
  "i",
);
const REFERENTIAL_FORWARD_EXCLUSION_PATTERN = new RegExp(
  String.raw`\b(?:(?:not|without|exclude|excluding|avoid(?:ing)?)\s+(?:(?:using|showing|charting|plotting|including)\s+)?(?:(?:a|the|any)\s+)?${FORWARD_PE_QUALIFIER_SOURCE}\b|(?:do\s+not|don't|never)\s+(?:use|show|chart|plot|include|create)\s+(?:(?:a|the|any)\s+)?${FORWARD_PE_QUALIFIER_SOURCE}\b)`,
  "i",
);

/**
 * Preserves an explicit P/E basis through referential follow-ups such as
 * "create that chart" without overriding a new trailing-only instruction.
 *
 * The current request remains first so callers can prefer its exact wording.
 * A direct trailing/LTM choice or forward exclusion intentionally suppresses
 * all supplemental context.
 */
export function resolveForwardPeIntentSignals(
  currentRequest: string | undefined,
  supplementalContext: readonly (string | undefined)[],
): string[] {
  const current =
    typeof currentRequest === "string" ? currentRequest.trim() : "";
  const positiveTrailingIntent = current.replace(
    TRAILING_PE_EXCLUSION_PATTERN,
    " ",
  );
  if (
    current &&
    !isExplicitForwardPeRequest(current) &&
    (TRAILING_PE_OVERRIDE_PATTERN.test(positiveTrailingIntent) ||
      REFERENTIAL_TRAILING_OVERRIDE_PATTERN.test(positiveTrailingIntent) ||
      REFERENTIAL_FORWARD_EXCLUSION_PATTERN.test(current))
  ) {
    return [current];
  }

  return [current, ...supplementalContext]
    .filter(
      (entry): entry is string =>
        typeof entry === "string" && entry.trim().length > 0,
    )
    .map((entry) => entry.trim());
}

/** Options for {@link suggestChartCapability}. */
export interface SuggestChartCapabilityOptions {
  /**
   * Restrict candidates to a single chart system. When set, the pool
   * is filtered BEFORE alias matching and capping, so a `native_excel`
   * caller cannot have its slots crowded out by FactSet entries that
   * matched first. Without this, `suggestChartCapability("price", 3)`
   * caps at three FactSet results and the caller's post-filter would
   * leave zero suggestions even though valid native_excel matches
   * exist.
   */
  system?: ChartSystem;
  /** Prefer this over `system` for model-facing output-mode selection. */
  outputMode?: ChartOutputMode;
}

/**
 * Suggest plausible capabilities for an unknown id or free-form user
 * phrase. Used when the validation gate rejects a chartType, to
 * surface "did you mean …?" alternatives in the user-facing error.
 *
 * Matching strategy (in order):
 *  1. Lowercased substring match against each alias.
 *  2. Lowercased substring match against displayName / description.
 *  3. Empty array if neither matches.
 *
 * Returns up to `maxSuggestions` matches. `opts.outputMode` scopes the
 * pool first and takes precedence over the legacy `opts.system`; with
 * neither set, FactSet capabilities are ordered first since most chart
 * requests are ticker-driven, and native Excel entries follow. An
 * explicit forward/NTM P/E query drops trailing/LTM capabilities from
 * the scoped pool.
 *
 * Known-unsupported chart families (e.g. Sankey, treemap) yield no
 * direct alias/text match. Callers that want a guaranteed
 * "closest-available" suggestion in that case should apply their own
 * fallback after this returns an empty array — see
 * `spreadsheetIntents.ts::buildUnknownChartTypeMessage` for the
 * canonical pattern.
 */
export function suggestChartCapability(
  query: string,
  maxSuggestions: number = 3,
  opts?: SuggestChartCapabilityOptions,
): readonly ChartCapability[] {
  if (typeof query !== "string" || query.length === 0) return [];
  const q = query.toLowerCase();
  const outputMode = opts?.outputMode;
  const scopedPool = outputMode
    ? CHART_CAPABILITIES.filter((c) => c.outputModes.includes(outputMode))
    : opts?.system
      ? CHART_CAPABILITIES.filter((c) => c.system === opts.system)
      : CHART_CAPABILITIES;
  const pool = isExplicitForwardPeRequest(query)
    ? scopedPool.filter((c) => c.metricBasis !== "trailing_ltm")
    : scopedPool;

  // `q.includes(a)` is the forward match (user typed a phrase that
  // contains an alias). `a.includes(q)` is the reverse match (user
  // typed a short fragment of a longer alias, e.g. "p/e" → "p/e
  // ratio"). Guard the reverse direction with `q.length >= 3` so a
  // 1–2 char fragment can't sweep up unrelated aliases. Short queries
  // still match short aliases verbatim via the forward path (e.g.
  // "fx" still finds the "fx" alias for P_FX_RATE). See Bugbot
  // PR-201 #3267590005.
  const aliasMatches = pool.filter((c) =>
    c.aliases.some((a) => q.includes(a) || (q.length >= 3 && a.includes(q))),
  );
  if (aliasMatches.length > 0) {
    return prioritiseAndCap(aliasMatches, maxSuggestions);
  }

  const textMatches = pool.filter(
    (c) =>
      c.displayName.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q),
  );
  return prioritiseAndCap(textMatches, maxSuggestions);
}

function prioritiseAndCap(
  matches: readonly ChartCapability[],
  cap: number,
): readonly ChartCapability[] {
  const factset = matches.filter((c) => c.system === "factset_activegraph");
  const native = matches.filter((c) => c.system === "native_excel");
  return [...factset, ...native].slice(0, cap);
}

/**
 * Detect whether a chart phrase points at a known-unsupported type.
 * Used by the dispatcher to give a more focused error message than
 * the generic "unknown chart type" reply.
 *
 * Example: `isKnownUnsupportedChartType("SANKEY")` -> `true`;
 * `isKnownUnsupportedChartType("create a sankey diagram")` -> `true`.
 */
export function isKnownUnsupportedChartType(query: string): boolean {
  if (typeof query !== "string" || query.length === 0) return false;
  const q = query.toLowerCase();
  return UNSUPPORTED_CHART_PATTERNS.some((p) => q.includes(p));
}

// ---------------------------------------------------------------------------
// Prompt-block rendering — derived from the registry so the prompt
// and the validation gate cannot drift. Replaces the inline prose
// block at `excelAIService.ts` lines 2255-2297.
// ---------------------------------------------------------------------------

/**
 * Render the FactSet ChartGPT catalog as the same Markdown-ish prose
 * the LLM saw before, grouped by category. Designed to drop in
 * verbatim wherever the inline 27-code block used to live.
 *
 * Deliberately FactSet-only: the planner names range-chart shapes in plain
 * words ("column", "line"), which `plannerChartTypeToOffice` maps to the `xl*`
 * enums, so listing those 24 codes here would teach it to emit identifiers the
 * chat path rejects. `list_chart_types` still surfaces them on request.
 */
export function buildFactSetCatalogPromptBlock(): string {
  const groups: Array<{ heading: string; category: ChartCategory }> = [
    { heading: "Price / volume / FX", category: "price_volume_fx" },
    {
      heading:
        "Valuation multiples (NB: `VAL_PE2` is trailing/LTM P/E, NOT forward/NTM P/E or `P_PE`)",
      category: "valuation",
    },
    { heading: "Market size", category: "size" },
    { heading: "Estimates / earnings (NB: \"EPS\" is `EST_EPS`, NOT `P_EPS`)", category: "estimates" },
    { heading: "Financials / segments", category: "financials" },
    { heading: "Fixed income", category: "fixed_income" },
  ];

  const lines: string[] = [];
  for (const g of groups) {
    const entries = listChartCapabilities({
      outputMode: "active_graph",
      category: g.category,
    });
    if (entries.length === 0) continue;
    lines.push(`${g.heading}:`);
    for (const e of entries) {
      lines.push(
        `- \`${e.id}\` — ${e.description} (${activeGraphEntityPromptLabel(e.activeGraphEntityCardinality)})`,
      );
    }
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}

/**
 * Render the three output modes from the registry. Chart type ids remain in the
 * shared catalog, so this overview cannot drift into a second type list.
 */
export function buildChartOutputModesPromptBlock(): string {
  return CHART_OUTPUT_MODES.map((mode) => {
    const layouts = mode.summaryTable.layouts;
    const summary = mode.summaryTable.supported
      ? ` Summary tables: ${mode.summaryTable.applicableChartTypeIds.length} applicable types, ` +
        `default ${mode.summaryTable.defaultDisplay}` +
        `${layouts ? `, layouts ${layouts.join("/")}` : ""}.`
      : " Summary tables: not supported in this mode.";
    return (
      `- \`${mode.id}\` (${mode.displayName}): ${mode.description} ` +
      `Data source: ${mode.dataSource}; lands in: ${mode.landingLocation}; ` +
      `operations: ${mode.supportedOperations.join(", ")}. ` +
      `Edits: ${mode.editSemantics}${summary}`
    );
  }).join("\n");
}

/**
 * Builds executor prompt guidance for broad ActiveGraph requests that should
 * become a small set of related on-sheet charts.
 */
export function buildFactSetMultiChartIntentPromptBlock(): string {
  return [
    "- If the user asks for a broad chart family, consider creating multiple useful ActiveGraphs rather than picking just one.",
    "- Prefer a small, coherent set from the relevant catalog category when the user names a broad theme instead of a single metric.",
    "- If the user names a specific metric or chart type, create only that requested chart unless they also ask for a broader view.",
    "- Use one chart creation call per ActiveGraph. Keep the ticker, date range, and company label consistent across the group.",
    "- Place related charts in a compact grid with modest gaps so they stay visually grouped without overlapping.",
  ].join("\n");
}

// ---------------------------------------------------------------------------
// User-facing error surfacing.
// ---------------------------------------------------------------------------

/**
 * Sentinel prefix on every client-side chart-capability rejection
 * message. Tool callers use `isCapabilityErrorMessage` to detect these
 * and render the message verbatim, instead of falling back to a generic
 * chart-unavailable placeholder used for host-side errors.
 *
 * Why a sentinel rather than a custom Error subclass: callers receive
 * chart failures as `error: string` on the `FactsetChartPreview`, which
 * loses subclass identity. A sentinel survives any JSON / string
 * round-trip between the wrapper, tool result, and renderer.
 *
 * The prefix is stripped by `stripCapabilityErrorPrefix` before
 * display, so users see a clean message.
 */
export const CHART_CAPABILITY_ERROR_PREFIX = "[chart-capability] ";

export function isCapabilityErrorMessage(message: string | null | undefined): boolean {
  return typeof message === "string" && message.startsWith(CHART_CAPABILITY_ERROR_PREFIX);
}

export function stripCapabilityErrorPrefix(message: string): string {
  return message.startsWith(CHART_CAPABILITY_ERROR_PREFIX)
    ? message.slice(CHART_CAPABILITY_ERROR_PREFIX.length)
    : message;
}

/**
 * Render the "do not attempt" list. Used in chart-emitting prompts
 * so the model declines unsupported requests with a helpful reply
 * instead of guessing a code the host will reject.
 */
export function buildUnsupportedPromptBlock(): string {
  return (
    `Chart types NOT supported — do NOT attempt these. ` +
    `If the user requests one, reply in chat naming the closest available alternative ` +
    `(see the FactSet ActiveGraph or native Excel catalogs above) or explain that this ` +
    `chart type is not available today: ` +
    UNSUPPORTED_CHART_PATTERNS.map((p) => `\`${p}\``).join(", ") +
    `.`
  );
}
